import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Button,
  IconButton, Tooltip
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const SOCIAL_PLATFORMS = [
  { key: "facebook_url",  label: "Facebook",   icon: <FacebookIcon sx={{ color: "#1877F2", fontSize: "28px" }} />,  placeholder: "https://facebook.com/cummingstamilschool" },
  { key: "instagram_url", label: "Instagram",  icon: <InstagramIcon sx={{ color: "#E1306C", fontSize: "28px" }} />, placeholder: "https://instagram.com/cummingstamilschool" },
  { key: "twitter_url",   label: "Twitter / X", icon: <TwitterIcon sx={{ color: "#1DA1F2", fontSize: "28px" }} />,  placeholder: "https://twitter.com/cummingstamilschool" },
  { key: "youtube_url",   label: "YouTube",    icon: <YouTubeIcon sx={{ color: "#FF0000", fontSize: "28px" }} />,   placeholder: "https://youtube.com/@cummingstamilschool" },
  { key: "linkedin_url",  label: "LinkedIn",   icon: <LinkedInIcon sx={{ color: "#0A66C2", fontSize: "28px" }} />,  placeholder: "https://linkedin.com/company/cummingstamilschool" },
];

const AdminSocialMedia = () => {
  const [values, setValues] = useState({});
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/website-config/list?category=social");
      if (res?.data) {
        const arr = Array.isArray(res.data) ? res.data : [];
        const init = {};
        arr.forEach(c => { init[c.configKey] = c.configValue || ""; });
        setValues(init);
      }
    } catch (_) {
      const init = {};
      SOCIAL_PLATFORMS.forEach(p => { init[p.key] = ""; });
      setValues(init);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const configs = SOCIAL_PLATFORMS.map(p => ({ configKey: p.key, configValue: values[p.key] || "" }));
    try {
      await putData("admin/website-config/bulk-update", { configs });
      setToast({ open: true, severity: "success", message: "Social media links saved!" });
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to save. Please try again." });
    }
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 8px" }}>
        Pages / Social Media Management
      </p>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <ShareIcon sx={{ color: "#244C77", fontSize: "28px" }} />
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>Social Media Links</Typography>
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Manage social media icons displayed in the website footer</Typography>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        {SOCIAL_PLATFORMS.map(platform => (
          <Grid item xs={12} sm={6} key={platform.key}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #F3F4F6" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                {platform.icon}
                <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827" }}>{platform.label}</Typography>
                {values[platform.key] && (
                  <Tooltip title="Open link">
                    <IconButton size="small" onClick={() => window.open(values[platform.key], "_blank")}
                      sx={{ ml: "auto", color: "#9CA3AF" }}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <TextField
                fullWidth size="small"
                value={values[platform.key] || ""}
                onChange={e => setValues(prev => ({ ...prev, [platform.key]: e.target.value }))}
                placeholder={platform.placeholder}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px", fontSize: "13px",
                    "& fieldset": { borderColor: "#D5D7DA" },
                    "&:hover fieldset": { borderColor: "#4A7BA8" },
                    "&.Mui-focused fieldset": { borderColor: "#244C77" },
                  }
                }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button startIcon={<SaveOutlinedIcon />} onClick={handleSave}
          sx={{
            background: "linear-gradient(90deg, #4A7BA8, #244C77)",
            color: "#fff", fontSize: "13px", textTransform: "none",
            borderRadius: "10px", px: 3, py: 1,
          }}>
          Save All Links
        </Button>
      </Box>
    </div>
  );
};

export default AdminSocialMedia;
